import React, { useState, useMemo } from 'react';
import { useTableDataWithPost } from '../../hooks/useTableWithPost';
import { RoleManagement } from '../../config/tableConfigs';
import RoleManagerFormModal from './components/roleManagementFormModal';
import DeleteModal from '../../components/DeleteModal';
import { useSession } from 'hooks/useSession';

const defaultRolesData = [
  { id: 1, Name: "Admin", status: "Active", users: 4, created_at: "10 Jan 2025" },
  { id: 2, Name: "White label", status: "Active", users: 0, created_at: "12 Jan 2025" },
  { id: 3, Name: "Master Distributor", status: "Active", users: 2, created_at: "15 Jan 2025" },
  { id: 5, Name: "Retailer", status: "Active", users: 1, created_at: "18 Jan 2025" },
  { id: 6, Name: "Main Admin", status: "Active", users: 3, created_at: "20 Jan 2025" },
  { id: 9, Name: "API Partner", status: "Active", users: 1, created_at: "22 Jan 2025" },
  { id: 16, Name: "Pancard Agent", status: "Active", users: 0, created_at: "25 Jan 2025" },
  { id: 17, Name: "Platform User", status: "Active", users: 8, created_at: "28 Jan 2025" },
  { id: 18, Name: "Payout API", status: "Active", users: 1, created_at: "30 Jan 2025" },
];

const getRoleIconDetails = (roleName = "") => {
  const nameLower = roleName.toLowerCase();
  if (nameLower.includes("main admin")) {
    return { icon: "ph-duotone ph-gear-six", color: "#16a34a" };
  } else if (nameLower.includes("admin")) {
    return { icon: "ph-duotone ph-crown-simple", color: "#7e57c2" };
  } else if (nameLower.includes("white label") || nameLower.includes("whitelabel")) {
    return { icon: "ph-duotone ph-tag", color: "#ef4444" };
  } else if (nameLower.includes("master distributor")) {
    return { icon: "ph-duotone ph-tree-structure", color: "#0284c7" };
  } else if (nameLower.includes("retailer")) {
    return { icon: "ph-duotone ph-wallet", color: "#f97316" };
  } else if (nameLower.includes("api partner")) {
    return { icon: "ph-duotone ph-code", color: "#7c3aed" };
  } else if (nameLower.includes("pancard")) {
    return { icon: "ph-duotone ph-user", color: "#eab308" };
  } else if (nameLower.includes("platform")) {
    return { icon: "ph-duotone ph-users-three", color: "#0d9488" };
  } else if (nameLower.includes("payout")) {
    return { icon: "ph-duotone ph-database", color: "#db2777" };
  }
  return { icon: "ph-duotone ph-shield-check", color: "#7e57c2" };
};

const RoleManagementPage = () => {
  const [modalShow, setModalShow] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedUser, setSelectedUser] = useState(null);
  const { userid } = useSession();
  const [refresh, setRefresh] = useState(0);

  const formdata = new FormData();
  formdata.append("action", "GET");
  formdata.append("userid", userid);

  const tableData = useTableDataWithPost(
    "https://pro.mokshiq.in/role_manager_action",
    RoleManagement,
    formdata,
    refresh
  );

  const {
    data,
    loading,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortField,
    sortDirection,
    handleSort,
    selectedRows,
    handleRowSelect,
    handleSelectAll,
  } = tableData;

  const rawList = useMemo(() => {
    if (Array.isArray(data) && data.length > 0) {
      return data;
    }
    return defaultRolesData;
  }, [data]);

  const filteredRoles = useMemo(() => {
    return rawList.filter((item) => {
      const name = item.Name || item.name || item.rolename || "";
      return name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [rawList, searchTerm]);

  const sortedRoles = useMemo(() => {
    return [...filteredRoles].sort((a, b) => {
      let aVal = a[sortField] || a.Name || a.id;
      let bVal = b[sortField] || b.Name || b.id;
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRoles, sortField, sortDirection]);

  const paginatedRoles = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedRoles.slice(start, start + itemsPerPage);
  }, [sortedRoles, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedRoles.length / itemsPerPage) || 1;

  const handleAddUser = () => {
    setModalMode("add");
    setSelectedUser(null);
    setModalShow(true);
  };

  const handleEditUser = (user) => {
    setModalMode("edit");
    setSelectedUser(user);
    setModalShow(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setTimeout(() => {
      const modalEl = document.getElementById("deleterole1");
      if (modalEl) {
        const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.show();
      }
    }, 100);
  };

  const handleFormSuccess = () => {
    console.log("Form action successful - refreshing...");
    setRefresh((prev) => prev + 1);
    setModalShow(false);
  };

  const handleDeleteSuccess = () => {
    console.log("Role deleted - refreshing...");
    setRefresh((prev) => prev + 1);
  };

  // Metrics
  const totalRoles = rawList.length;
  const activeRoles = rawList.filter(
    (r) => r.status === "Active" || r.status === true || r.Status === "Active" || r.Status === true || r.status === 1
  ).length || totalRoles;
  const systemRoles = 3;
  const customRoles = Math.max(0, totalRoles - systemRoles) || 7;

  return (
    <main style={{ backgroundColor: "#f6f8fb", minHeight: "100vh", padding: "24px 16px" }}>
      <div className="container-fluid max-w-1400">
        {/* Top Header & Breadcrumbs */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h3 className="fw-bold mb-1" style={{ color: "#1e1e2d", fontSize: "1.75rem", letterSpacing: "-0.5px" }}>
              Role Management
            </h3>
            <div className="d-flex align-items-center gap-2" style={{ fontSize: "13.5px", fontWeight: "500" }}>
              <a href="#" className="text-decoration-none d-flex align-items-center gap-1" style={{ color: "#7e57c2" }}>
                <i className="ph-duotone ph-house f-s-16"></i>
              </a>
              <span style={{ color: "#cbd5e1" }}>/</span>
              <a href="#" className="text-decoration-none" style={{ color: "#7e57c2" }}>
                Account Management
              </a>
              <span style={{ color: "#cbd5e1" }}>&gt;</span>
              <span style={{ color: "#64748b" }}>Role Management</span>
            </div>
          </div>

          <div>
            <button
              className="btn d-inline-flex align-items-center gap-2 px-4 shadow-sm"
              onClick={handleAddUser}
              style={{
                backgroundColor: "#7e57c2",
                borderColor: "#7e57c2",
                color: "#ffffff",
                borderRadius: "10px",
                height: "44px",
                fontWeight: "600",
                fontSize: "14px",
                transition: "all 0.2s"
              }}
            >
              <i className="ph-bold ph-plus-circle" style={{ fontSize: "18px" }}></i>
              <span>Add Role</span>
            </button>
          </div>
        </div>

        {/* 4 Summary Stat Cards */}
        <div className="row g-3 mb-4">
          {/* Card 1: Total Roles */}
          <div className="col-12 col-sm-6 col-xl-3">
            <div
              className="card border-0 shadow-sm p-3 h-100 position-relative overflow-hidden"
              style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "14px",
                    backgroundColor: "#f3e8ff",
                    color: "#7e57c2"
                  }}
                >
                  <i className="ph-duotone ph-users-three" style={{ fontSize: "26px" }}></i>
                </div>
                <div>
                  <div className="text-muted fw-medium" style={{ fontSize: "13px" }}>
                    Total Roles
                  </div>
                  <div className="fw-bold" style={{ fontSize: "22px", color: "#1e293b", lineHeight: "1.2" }}>
                    {totalRoles}
                  </div>
                </div>
              </div>
              <i
                className="ph-duotone ph-users position-absolute"
                style={{ right: "-10px", bottom: "-10px", fontSize: "70px", opacity: "0.05", color: "#7e57c2", pointerEvents: "none" }}
              ></i>
            </div>
          </div>

          {/* Card 2: Active Roles */}
          <div className="col-12 col-sm-6 col-xl-3">
            <div
              className="card border-0 shadow-sm p-3 h-100 position-relative overflow-hidden"
              style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "14px",
                      backgroundColor: "#dcfce7",
                      color: "#16a34a"
                    }}
                  >
                    <i className="ph-duotone ph-shield-check" style={{ fontSize: "26px" }}></i>
                  </div>
                  <div>
                    <div className="text-muted fw-medium" style={{ fontSize: "13px" }}>
                      Active Roles
                    </div>
                    <div className="fw-bold" style={{ fontSize: "22px", color: "#1e293b", lineHeight: "1.2" }}>
                      {activeRoles}
                    </div>
                  </div>
                </div>
                <div>
                  <span
                    className="badge rounded-pill px-2.5 py-1"
                    style={{ backgroundColor: "#e6f4ea", color: "#16a34a", fontSize: "12px", fontWeight: "600" }}
                  >
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: System Roles */}
          <div className="col-12 col-sm-6 col-xl-3">
            <div
              className="card border-0 shadow-sm p-3 h-100 position-relative overflow-hidden"
              style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "14px",
                    backgroundColor: "#ffedd5",
                    color: "#ea580c"
                  }}
                >
                  <i className="ph-duotone ph-user" style={{ fontSize: "26px" }}></i>
                </div>
                <div>
                  <div className="text-muted fw-medium" style={{ fontSize: "13px" }}>
                    System Roles
                  </div>
                  <div className="fw-bold" style={{ fontSize: "22px", color: "#1e293b", lineHeight: "1.2" }}>
                    {systemRoles}
                  </div>
                </div>
              </div>
              <i
                className="ph-duotone ph-crown position-absolute"
                style={{ right: "-10px", bottom: "-10px", fontSize: "70px", opacity: "0.06", color: "#ea580c", pointerEvents: "none" }}
              ></i>
            </div>
          </div>

          {/* Card 4: Custom Roles */}
          <div className="col-12 col-sm-6 col-xl-3">
            <div
              className="card border-0 shadow-sm p-3 h-100 position-relative overflow-hidden"
              style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "14px",
                    backgroundColor: "#dbeafe",
                    color: "#2563eb"
                  }}
                >
                  <i className="ph-duotone ph-users-four" style={{ fontSize: "26px" }}></i>
                </div>
                <div>
                  <div className="text-muted fw-medium" style={{ fontSize: "13px" }}>
                    Custom Roles
                  </div>
                  <div className="fw-bold" style={{ fontSize: "22px", color: "#1e293b", lineHeight: "1.2" }}>
                    {customRoles}
                  </div>
                </div>
              </div>
              <i
                className="ph-duotone ph-gear position-absolute"
                style={{ right: "-10px", bottom: "-10px", fontSize: "70px", opacity: "0.06", color: "#2563eb", pointerEvents: "none" }}
              ></i>
            </div>
          </div>
        </div>

        {/* Table Container Card */}
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4" style={{ backgroundColor: "#ffffff" }}>
          {/* Inner Header Bar */}
          <div className="d-flex align-items-center justify-content-between p-4 border-bottom border-light flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ width: "44px", height: "44px", borderRadius: "12px", backgroundColor: "#f3e8ff", color: "#7e57c2" }}
              >
                <i className="ph-duotone ph-users-three" style={{ fontSize: "24px" }}></i>
              </div>
              <div>
                <h5 className="fw-bold mb-0" style={{ color: "#1e293b", fontSize: "17px" }}>
                  Role Management
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                  Manage system roles and permissions
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Search input */}
              <div className="position-relative" style={{ width: "240px" }}>
                <i
                  className="ph-bold ph-magnifying-glass position-absolute"
                  style={{ left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "16px" }}
                ></i>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by role name..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    paddingLeft: "38px",
                    borderRadius: "10px",
                    borderColor: "#e2e8f0",
                    height: "40px",
                    fontSize: "13.5px"
                  }}
                />
              </div>

              {/* Items Per Page Select */}
              <select
                className="form-select"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  width: "80px",
                  borderRadius: "10px",
                  borderColor: "#e2e8f0",
                  height: "40px",
                  fontSize: "13.5px"
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>

              {/* Filter Button */}
              <button
                className="btn d-inline-flex align-items-center gap-2 border"
                style={{
                  borderRadius: "10px",
                  borderColor: "#e2e8f0",
                  height: "40px",
                  backgroundColor: "#ffffff",
                  color: "#475569",
                  fontSize: "13.5px",
                  fontWeight: "500"
                }}
              >
                <i className="ph-bold ph-funnel" style={{ fontSize: "15px" }}></i>
                <span>Filter</span>
                <i className="ph-bold ph-caret-down" style={{ fontSize: "12px", color: "#94a3b8" }}></i>
              </button>
            </div>
          </div>

          {/* Table Body */}
          <div className="table-responsive">
            <table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: "0" }}>
              <thead style={{ backgroundColor: "#f8fafc" }}>
                <tr>
                  <th style={{ width: "40px", padding: "14px 16px", borderBottom: "1px solid #e2e8f0" }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      style={{ borderRadius: "4px" }}
                      checked={selectedRows.length === paginatedRoles.length && paginatedRoles.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th
                    onClick={() => handleSort("id")}
                    style={{
                      cursor: "pointer",
                      padding: "14px 16px",
                      color: "#64748b",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderBottom: "1px solid #e2e8f0"
                    }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      <span>ID</span>
                      <i className="ph-bold ph-arrows-down-up" style={{ fontSize: "12px", color: "#94a3b8" }}></i>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("Name")}
                    style={{
                      cursor: "pointer",
                      padding: "14px 16px",
                      color: "#64748b",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderBottom: "1px solid #e2e8f0"
                    }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      <span>Role Name</span>
                      <i className="ph-bold ph-arrows-down-up" style={{ fontSize: "12px", color: "#94a3b8" }}></i>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    style={{
                      cursor: "pointer",
                      padding: "14px 16px",
                      color: "#64748b",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderBottom: "1px solid #e2e8f0"
                    }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      <span>Status</span>
                      <i className="ph-bold ph-arrows-down-up" style={{ fontSize: "12px", color: "#94a3b8" }}></i>
                    </div>
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      color: "#64748b",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderBottom: "1px solid #e2e8f0"
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" style={{ color: "#7e57c2" }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedRoles.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      No roles found matching your search.
                    </td>
                  </tr>
                ) : (
                  paginatedRoles.map((item, index) => {
                    const roleName = item.Name || item.name || item.rolename || `Role #${item.id}`;
                    const iconMeta = getRoleIconDetails(roleName);
                    const isSelected = selectedRows.includes(item.id);

                    return (
                      <tr
                        key={item.id}
                        style={{
                          backgroundColor: isSelected ? "#f8f5ff" : "transparent",
                          transition: "background-color 0.15s"
                        }}
                      >
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            style={{ borderRadius: "4px" }}
                            checked={isSelected}
                            onChange={() => handleRowSelect(item.id)}
                          />
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9", color: "#475569", fontSize: "14px", fontWeight: "600" }}>
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
                          <div className="d-flex align-items-center gap-2">
                            <i className={iconMeta.icon} style={{ color: iconMeta.color, fontSize: "18px" }}></i>
                            <span style={{ color: "#7e57c2", fontWeight: "600", fontSize: "14px" }}>
                              #{roleName}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
                          <span
                            className="badge rounded-pill d-inline-flex align-items-center gap-1.5 px-3 py-1.5"
                            style={{ backgroundColor: "#e6f4ea", color: "#16a34a", fontSize: "12px", fontWeight: "600" }}
                          >
                            <span
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: "#16a34a",
                                display: "inline-block"
                              }}
                            ></span>
                            Active
                          </span>
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
                          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                            <button
                              className="btn p-0 d-inline-flex align-items-center justify-content-center text-white"
                              style={{
                                width: "34px",
                                height: "34px",
                                borderRadius: "8px",
                                backgroundColor: "#7e57c2",
                                border: "none"
                              }}
                              title="Edit Role"
                              onClick={() => handleEditUser(item)}
                            >
                              <i className="ph-bold ph-pencil-simple" style={{ fontSize: "16px" }}></i>
                            </button>
                            <button
                              className="btn p-0 d-inline-flex align-items-center justify-content-center text-white"
                              style={{
                                width: "34px",
                                height: "34px",
                                borderRadius: "8px",
                                backgroundColor: "#ff3b30",
                                border: "none"
                              }}
                              title="Delete Role"
                              onClick={() => handleDelete(item)}
                            >
                              <i className="ph-bold ph-trash" style={{ fontSize: "16px" }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer / Pagination */}
          <div className="d-flex align-items-center justify-content-between p-4 border-top border-light flex-wrap gap-3">
            <div style={{ color: "#64748b", fontSize: "13.5px", fontWeight: "500" }}>
              Showing {sortedRoles.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, sortedRoles.length)} of {sortedRoles.length} entries
            </div>

            <div className="d-flex align-items-center">
              <button
                className="btn btn-light px-3 py-1.5"
                style={{
                  borderRadius: "8px 0 0 8px",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  color: currentPage === 1 ? "#cbd5e1" : "#475569",
                  fontSize: "13px",
                  fontWeight: "500"
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  className="btn px-3 py-1.5"
                  style={{
                    borderRadius: "0",
                    border: "1px solid #e2e8f0",
                    borderLeft: "none",
                    backgroundColor: currentPage === pg ? "#7e57c2" : "#ffffff",
                    color: currentPage === pg ? "#ffffff" : "#475569",
                    fontSize: "13px",
                    fontWeight: currentPage === pg ? "600" : "500"
                  }}
                  onClick={() => setCurrentPage(pg)}
                >
                  {pg}
                </button>
              ))}

              <button
                className="btn btn-light px-3 py-1.5"
                style={{
                  borderRadius: "0 8px 8px 0",
                  border: "1px solid #e2e8f0",
                  borderLeft: "none",
                  backgroundColor: "#ffffff",
                  color: currentPage === totalPages || totalPages === 0 ? "#cbd5e1" : "#475569",
                  fontSize: "13px",
                  fontWeight: "500"
                }}
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <RoleManagerFormModal
        show={modalShow}
        onClose={() => setModalShow(false)}
        mode={modalMode}
        userData={selectedUser}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        deleteid={selectedUser}
        id="deleterole1"
        title="Delete Role"
        message="Are you sure you want to delete this role?"
        apiUrl="https://pro.mokshiq.in/role_manager_action"
        onSuccess={handleDeleteSuccess}
      />
    </main>
  );
};

export default RoleManagementPage;

